/**
 * StrategyDivergenceChip — two-axis divergence indicator (Spec A / D51, A5-2).
 *
 * Shows, on BOTH editors, when the strategy version axis and the compiled
 * onboarding have drifted apart:
 *  - strategy side: the working head moved past what was compiled
 *    (draft edits after compile, or compiled from an older baked version);
 *  - onboarding side: the version was compiled from an older strategy version,
 *    or the linked strategy has unbaked draft changes.
 *
 * Noise guard: renders NOTHING unless the strategy has been baked at least once
 * (`currentVersion` non-empty) AND a compile link exists — legacy/never-compiled
 * strategies must not light up every canvas.
 *
 * Follows the `StrategyLineageChip` presentation pattern (small outlined chip).
 */
import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import type { StrategyStatus } from '@segmently/schema-models/strategy';

export type DivergenceSide = 'strategy' | 'onboarding';

export interface DivergenceInput {
  side: DivergenceSide;
  /** True when a compile link exists (strategy side) / the version carries sourceStrategyId (onboarding side). */
  linked: boolean;
  strategyStatus?: StrategyStatus | null;
  /** The strategy head's current baked version id ('' when never baked). */
  strategyCurrentVersion?: string | null;
  /** The strategy version the onboarding was compiled from. */
  compiledFromVersion?: string | null;
}

export interface DivergenceVerdict {
  label: string;
  detail: string;
}

const short = (id?: string | null): string => (id ? `${id.slice(0, 6)}…` : '?');

/** Pure rule — exported for unit tests. Returns null when nothing diverged. */
export function computeDivergence(input: DivergenceInput): DivergenceVerdict | null {
  const current = input.strategyCurrentVersion ?? '';
  if (!input.linked || !current) return null;
  const compiledFrom = input.compiledFromVersion ?? '';

  if (input.side === 'strategy') {
    if (input.strategyStatus === 'draft') {
      return {
        label: 'Edits not compiled yet',
        detail: 'The strategy head has draft changes newer than the compiled onboarding. Bake and recompile (or push) to sync.',
      };
    }
    if (compiledFrom && compiledFrom !== current) {
      return {
        label: 'Onboarding behind',
        detail: `The linked onboarding was compiled from version ${short(compiledFrom)}, but the strategy is now at ${short(current)}. Recompile to update it.`,
      };
    }
    return null;
  }

  // onboarding side
  if (compiledFrom && compiledFrom !== current) {
    return {
      label: 'Strategy moved ahead',
      detail: `This version was compiled from strategy version ${short(compiledFrom)}; the strategy is now at ${short(current)}. Recompile or pull to sync.`,
    };
  }
  if (input.strategyStatus === 'draft') {
    return {
      label: 'Strategy has draft changes',
      detail: 'The linked strategy has unbaked draft edits that are not reflected in this onboarding.',
    };
  }
  return null;
}

export interface StrategyDivergenceChipProps extends DivergenceInput {}

export const StrategyDivergenceChip: React.FC<StrategyDivergenceChipProps> = (props) => {
  const verdict = computeDivergence(props);
  if (!verdict) return null;
  return (
    <Tooltip title={verdict.detail}>
      <Chip
        size="small"
        variant="outlined"
        color="warning"
        icon={<CompareArrowsIcon />}
        label={verdict.label}
        data-testid={`strategy-divergence-chip-${props.side}`}
      />
    </Tooltip>
  );
};

export default StrategyDivergenceChip;
